import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';
import { BlockSchema, CoverImageSchema } from './block';

/**
 * Post Schema
 * Organization homepage announcements ("posts feed"). The public org home
 * page renders the org's PUBLISHED posts newest-`publishedAt`-first; an org
 * with zero published posts falls back to its legacy Home Layout html.
 * Status follows the conventional CMS model: new posts start as 'draft'
 * (hidden from the public feed); "Unpublish" returns a post to draft.
 * The rich-text body is stored as Quill-native HTML in `html` (same
 * contract as Layout.html — edited by the admin Page Editor).
 */

export const PostSchema = z.object({
  _id: objectId,
  title: z.string(),
  html: z.string().default(''),
  // Display/sort date shown on the public feed (defaults to creation time).
  // Always used for ORDERING, even when `showDate` hides it from the card.
  publishedAt: z.coerce.date().optional(),
  // Per-post header visibility. Some posts are purely visual (a sponsor board,
  // a flyer image) and read better with no title or date above them.
  // Mongoose defaults apply to NEW documents only, so posts created before
  // these fields existed have them ABSENT — consumers must treat `undefined`
  // as "show" (`post.showTitle !== false`), never as falsy.
  showTitle: z.boolean().default(true),
  showDate: z.boolean().default(true),
  status: z.enum(['published', 'draft']).default('draft'),
  // Pinned post (INT-0048): at most ONE per organization (the API unpins the
  // siblings on every `true` write). The public feed renders it first and in
  // full (no clamp, no "Read more"). Optional with NO default: absent means
  // not pinned, and older posts never get a stamped field.
  pinned: z.boolean().optional(),
  // Block-based body (ADR 0033). Optional with NO default: absent means "not
  // migrated" and the renderer falls back to legacy `html`. The Mongoose model
  // must pin `default: undefined` — an implicit [] would read as "migrated,
  // empty". Stored as [Mixed]; validated only by BlocksSchema at the API
  // boundary.
  blocks: z.array(BlockSchema).optional(),
  // Explicit cover (blocks era). Unmigrated posts keep the render-time
  // lead-image convention (extractCover) instead.
  coverImage: CoverImageSchema.optional(),
  organization: objectId,
  ...timestampFields,
});

export type Post = z.infer<typeof PostSchema>;

export const postMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  title: { label: 'Title', editable: true },
  html: { label: 'Body (HTML)', editable: true },
  publishedAt: { label: 'Published At', editable: true },
  showTitle: { label: 'Show Title', editable: true },
  showDate: { label: 'Show Date', editable: true },
  status: { label: 'Status', editable: true },
  pinned: { label: 'Pinned', editable: true },
  blocks: { label: 'Blocks', editable: true },
  coverImage: { label: 'Cover Image', editable: true },
  organization: { ref: 'Organization', label: 'Organization' },
};
